import { bloki, glava, golo, type Blok } from './markdown';

/**
 * Članki pomoči (»navodila«): ena datoteka Markdown v {@code clanki/} na temo, ime datoteke je id (povezava
 * {@code /pomoc/<id>}, javno {@code /navodila/<id>}). Glava: naslov, opis, kategorija, kljucne (z vejico), novo (datum,
 * ko je funkcija prišla – članek je med novostmi). Slike so v {@code public/slike-pomoci/} (ustvari jih
 * {@code npm run pomoc:slike}).
 */
export type Clanek = {
  id: string;
  naslov: string;
  opis: string;
  kategorija: string;
  kljucne: string[];
  /** Datum (ISO), ko je funkcija prišla; članek je med novostmi. */
  novo?: string;
  vsebina: Blok[];
  /** Golo besedilo za iskanje. */
  besedilo: string;
};

/** Vrstni red kategorij na strani pomoči. */
export const KATEGORIJE = [
  'Vozila',
  'Delavnica',
  'Računi in predračuni',
  'Dokumenti in izpisi',
  'Kupci in prodajalci',
  'Javna ponudba in povpraševanja',
  'Umetna inteligenca',
  'Nastavitve in uporabniki',
];

/** Članek iz Markdowna z glavo. */
export function preberi(id: string, md: string): Clanek {
  const { glava: g, telo } = glava(md);
  const vsebina = bloki(telo);
  const besedilo = vsebina
    .map((b) => (b.t === 'seznam' ? b.postavke.map(golo).join(' ') : b.t === 'slika' ? b.opis : golo(b.v)))
    .join(' ');
  return {
    id,
    naslov: g.naslov ?? id,
    opis: g.opis ?? '',
    kategorija: g.kategorija ?? 'Drugo',
    kljucne: (g.kljucne ?? '')
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean),
    novo: g.novo || undefined,
    vsebina,
    besedilo,
  };
}

const datoteke = import.meta.glob('./clanki/*.md', { query: '?raw', import: 'default', eager: true }) as Record<
  string,
  string
>;

/** Vsi članki, po kategorijah (vrstni red {@link KATEGORIJE}) in naslovu. */
export const CLANKI: Clanek[] = Object.entries(datoteke)
  .map(([pot, md]) => preberi(pot.replace(/^.*\/|\.md$/g, ''), md))
  .sort((a, b) => vrstniRed(a.kategorija) - vrstniRed(b.kategorija) || a.naslov.localeCompare(b.naslov, 'sl'));

function vrstniRed(kategorija: string) {
  const i = KATEGORIJE.indexOf(kategorija);
  return i < 0 ? KATEGORIJE.length : i;
}

export function clanek(id: string | undefined): Clanek | undefined {
  return CLANKI.find((c) => c.id === id);
}

/** Male črke brez šumnikov (»Račun« najde tudi »racun«). */
export function normaliziraj(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * Članki, ki vsebujejo vse besede iskanja (naslov, opis, ključne besede, besedilo); najprej tisti z zadetkom v naslovu
 * ali ključnih besedah.
 */
export function isci(clanki: Clanek[], niz: string): Clanek[] {
  const besede = normaliziraj(niz).split(/\s+/).filter(Boolean);
  if (besede.length === 0) {
    return clanki;
  }
  const ocena = (c: Clanek) => {
    const glavno = normaliziraj([c.naslov, c.kljucne.join(' ')].join(' '));
    const vse = normaliziraj([c.naslov, c.opis, c.kljucne.join(' '), c.besedilo].join(' '));
    if (!besede.every((b) => vse.includes(b))) {
      return -1;
    }
    return besede.filter((b) => glavno.includes(b)).length;
  };
  return clanki
    .map((c) => ({ c, o: ocena(c) }))
    .filter((x) => x.o >= 0)
    .sort((a, b) => b.o - a.o)
    .map((x) => x.c);
}

/** Novosti: članki z datumom, najnovejši prvi. */
export function novosti(clanki: Clanek[]): Clanek[] {
  return clanki.filter((c) => c.novo).sort((a, b) => b.novo!.localeCompare(a.novo!));
}
